'use client';

import { useState } from 'react';
import {
  Title,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Stack,
  Anchor,
  Group,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconEye, IconEyeOff, IconShieldCheck } from '@tabler/icons-react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import Section from '@/components/ui/Section';

export default function SignIn() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Use custom 2FA login endpoint
    const res = await fetch('/api/auth/login-with-2fa', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        twoFactorToken: requiresTwoFactor ? twoFactorToken : undefined,
      }),
    });

    const data = await res.json();
    setSubmitting(false);

    if (data.requiresTwoFactor) {
      // 2FA is required
      setRequiresTwoFactor(true);
      return;
    }

    if (!res.ok) {
      setError(
        data.error === 'Too many attempts'
          ? 'Account temporarily locked. Try again later.'
          : data.error || 'Invalid credentials'
      );
      return;
    }

    // Success - use NextAuth signIn to establish session
    const signInRes = await signIn('credentials', {
      email,
      password,
      redirect: false,
    });

    if (signInRes?.ok) {
      router.replace('/');
    } else {
      setError('Login failed. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    setRequiresTwoFactor(false);
    setTwoFactorToken('');
    setError('');
  };

  return (
    <Section bg="themeSignIn" contentWidth="xs" contentAlign="start" py="sm">
      <Title order={2} mt={{ base: 'xl', sm: 0 }}>Sign in</Title>

      {error && (
        <Alert
          mt="md"
          variant="filled"
          color="red"
          icon={<IconAlertTriangle size={18} />}
          withCloseButton
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack mt="md">
          {requiresTwoFactor ? (
            <>
              <Alert color="blue" icon={<IconShieldCheck size={18} />}>
                <Text size="sm" fw={500} mb={4}>
                  Two-factor authentication required
                </Text>
                <Text size="xs">
                  Enter the 6-digit code from your authenticator app or use a backup code.
                </Text>
              </Alert>

              <TextInput
                label="Authentication Code"
                description="6-digit code from your app, or 10-character backup code"
                placeholder="123456 or ABC1234567"
                value={twoFactorToken}
                onChange={(e) => setTwoFactorToken(e.currentTarget.value.toUpperCase())}
                maxLength={10}
                styles={{ input: { fontFamily: 'monospace', letterSpacing: '0.1em' } }}
                autoComplete="one-time-code"
              />

              <Group justify="space-between" mt="md">
                <Anchor
                  component="button"
                  type="button"
                  c="navy.7"
                  size="sm"
                  onClick={handleBackToLogin}
                >
                  ← Back to login
                </Anchor>
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={twoFactorToken.length === 0}
                >
                  Verify
                </Button>
              </Group>
            </>
          ) : (
            <>
              <TextInput
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoComplete="username"
              />

              <PasswordInput
                label="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                visibilityToggleIcon={({ reveal }) =>
                  reveal ? <IconEyeOff size={16} /> : <IconEye size={16} />
                }
                autoComplete="current-password"
              />

              <Group justify="space-between" mt="xs">
                <Anchor c="navy.7" href="/auth/forgot-password" size="sm">
                  Forgot password?
                </Anchor>
                <Button type="submit" loading={submitting}>
                  Sign in
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </form>
    </Section>
  );
}
